// pages/OpenWeb.jsx
import { useNavigate } from 'react-router-dom';

export default function OpenWeb() {
  const navigate = useNavigate();

  const features = [
    {
      icon: "🌐",
      title: "Programmatic Access",
      description: "Connect to all major DSPs and ad exchanges in one platform."
    },
    {
      icon: "🎯",
      title: "Contextual Targeting",
      description: "Reach audiences based on content relevance and user intent."
    },
    {
      icon: "📊",
      title: "Real-Time Bidding",
      description: "Optimize bids in real-time to maximize campaign efficiency."
    },
    {
      icon: "🔒",
      title: "Brand Safety",
      description: "Ensure your ads appear only on premium, brand-safe sites."
    },
    {
      icon: "💰",
      title: "Cost Efficiency",
      description: "Lower CPMs while maintaining quality inventory access."
    },
    {
      icon: "📈",
      title: "Scale & Reach",
      description: "Access billions of impressions across the open web."
    }
  ];

  const dsps = [
    "Google DV360", "The Trade Desk", "Amazon DSP", "Yahoo DSP",
    "Xandr", "MediaMath", "StackAdapt", "Adobe Advertising Cloud"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00809D]/20 via-transparent to-transparent pointer-events-none" />
        
        <div className="max-w-6xl mx-auto relative">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Open Web
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Run personalized, high-impact ads across all major DSPs.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
                Launch Campaign
              </button>
              <button className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all">
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-gradient-to-br from-[#00809D]/20 to-transparent backdrop-blur-lg border border-white/10 rounded-2xl p-8">
              <div className="text-5xl font-bold text-[#00a0c4] mb-2">5B+</div>
              <div className="text-gray-400">Daily Impressions</div>
            </div>
            <div className="bg-gradient-to-br from-[#00809D]/20 to-transparent backdrop-blur-lg border border-white/10 rounded-2xl p-8">
              <div className="text-5xl font-bold text-[#00a0c4] mb-2">50K+</div>
              <div className="text-gray-400">Premium Publishers</div>
            </div>
            <div className="bg-gradient-to-br from-[#00809D]/20 to-transparent backdrop-blur-lg border border-white/10 rounded-2xl p-8">
              <div className="text-5xl font-bold text-[#00a0c4] mb-2">180</div>
              <div className="text-gray-400">Countries Covered</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Open Web Features
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DSPs Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#00809D]/10 to-transparent">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Integrated DSPs
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {dsps.map((dsp, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition-all"
              >
                {dsp}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-[#00809D]/20 to-[#00a0c4]/20 backdrop-blur-lg border border-white/20 rounded-3xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Unlock the Power of Open Web
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Scale your reach with programmatic advertising across premium publishers.
          </p>
          <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-10 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
            Get Started Now
          </button>
        </div>
      </section>
    </div>
  );
}
